import React from "react";

import {
  SPOKE_END_RADIUS,
  SPOKE_START_RADIUS,
  SPOKE_WHEEL_CENTRE,
} from "../constants/spokeField";
import { SpokeLine } from "../types/SpokeLine";
import { SpokeWheel } from "../types/SpokeWheel";
import { SpokeWheelCorner } from "../types/SpokeWheelCornerEnum";

/** A full turn, in radians. */
const FULL_TURN = Math.PI * 2;

/**
 * Spaces `spokeCount` spokes evenly around the hub.
 *
 * Computed rather than written out: the spoke count is the one number worth
 * tuning on this backdrop, and thirty-six hand-authored coordinate pairs would
 * make changing it a rewrite.
 *
 * Output shape: `[{ x1: 224, y1: 200, x2: 384, y2: 200 }, ...]`, one entry per
 * spoke, in viewBox units.
 *
 * @param params.spokeCount - How many spokes to space around the wheel
 * @returns The spokes, starting at three o'clock and running clockwise
 */
export const resolveSpokeLines = ({ spokeCount }: { spokeCount: number }): SpokeLine[] => {
  const spokeLines: SpokeLine[] = [];

  for (let spokeIndex = 0; spokeIndex < spokeCount; spokeIndex += 1) {
    const angle = (spokeIndex / spokeCount) * FULL_TURN;

    spokeLines.push({
      x1: SPOKE_WHEEL_CENTRE + Math.cos(angle) * SPOKE_START_RADIUS,
      y1: SPOKE_WHEEL_CENTRE + Math.sin(angle) * SPOKE_START_RADIUS,
      x2: SPOKE_WHEEL_CENTRE + Math.cos(angle) * SPOKE_END_RADIUS,
      y2: SPOKE_WHEEL_CENTRE + Math.sin(angle) * SPOKE_END_RADIUS,
    });
  }

  return spokeLines;
};

/**
 * Hangs a wheel off the corner it belongs to.
 *
 * The wheel is the smaller of its pixel size and a share of viewport width, and
 * both offsets are shares of whichever won. That keeps the same composition on
 * a phone as on a desktop: a wheel sized in fixed pixels covers a quarter of a
 * wide screen and most of a narrow one.
 *
 * The offsets are negative, so the hub lands outside the viewport whichever
 * corner it is measured from. Naming the edges per corner — top/right for one,
 * bottom/left for the other — is what lets one pair of numbers mean the same
 * thing on both wheels.
 *
 * @param params.wheel - The wheel being placed
 * @returns Absolute-position styles for the wheel's element
 */
export const resolveWheelPlacement = ({ wheel }: { wheel: SpokeWheel }): React.CSSProperties => {
  const edge = `min(${wheel.maxSizePx}px, ${wheel.viewportWidthPercent}vw)`;
  const size: React.CSSProperties = {
    width: edge,
    height: edge,
    opacity: wheel.opacity,
  };

  if (wheel.corner === SpokeWheelCorner.TopRight) {
    return {
      ...size,
      top: `calc(${edge} * ${wheel.verticalOffsetRatio})`,
      right: `calc(${edge} * ${wheel.horizontalOffsetRatio})`,
    };
  }

  return {
    ...size,
    bottom: `calc(${edge} * ${wheel.verticalOffsetRatio})`,
    left: `calc(${edge} * ${wheel.horizontalOffsetRatio})`,
  };
};
